import { useEffect, useState } from 'react';
import { Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';

interface SkipWithNoteModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (note: string) => Promise<void> | void;
  participantName?: string;
  sessionNumber?: number;
}

/**
 * MED-738 skip-with-note modal (AC36-AC40).
 * Single optional note field; Cancel closes without change; Save & Skip
 * persists skip status + note on the queue entry.
 */
export function SkipWithNoteModal({
  opened,
  onClose,
  onConfirm,
  participantName,
  sessionNumber,
}: SkipWithNoteModalProps) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (opened) setNote('');
  }, [opened]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onConfirm(note.trim());
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const contextLine =
    participantName && sessionNumber
      ? `${participantName}'s Session ${sessionNumber} follow-up`
      : participantName ?? '';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Skip Follow-Up"
      centered
      size="md"
      zIndex={400}
    >
      <Stack gap="sm">
        {contextLine && (
          <Text size="sm" c="dimmed">
            Skipping {contextLine}. The email will not be sent.
          </Text>
        )}
        <Textarea
          label="Why are you skipping? (optional)"
          placeholder="e.g. Participant emailed coach directly"
          value={note}
          onChange={e => setNote(e.currentTarget.value)}
          minRows={3}
          autosize
        />
        <Group justify="flex-end" gap="xs" mt="xs">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button color="medleyGreen" onClick={handleSubmit} loading={submitting}>
            Save &amp; Skip
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default SkipWithNoteModal;
